"use client";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* --------------------- Breakpoint hook --------------------- */
function useBreakpoint(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    // modern browsers: addEventListener
    try {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    } catch {
      // Safari < 14 fallback
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
  }, [query]);

  return matches;
}

/* --------------------- Component --------------------- */
const FilterSidebar = ({ onFilterChange }) => {
  const isDesktop = useBreakpoint("(min-width: 1280px)"); // xl breakpoint
  const [isOpen, setIsOpen] = useState(false);

  // whenever breakpoint flips, auto-open on desktop, auto-close on mobile
  useEffect(() => {
    setIsOpen(isDesktop);
  }, [isDesktop]);

  const [filters, setFilters] = useState({
    categories: {
      "Chaat Time": false,
      "Tea & Snacks": false,
      Milkshakes: false,
      Puffs: false,
      "Cakes (1 KG)": false,
      Pastries: false,
      "Special Cakes": false,
      "Jar Cakes": false,
      Brownies: false,
      "Tres Leches": false,
      "Custom Cakes": false,
      "Wedding Cakes": false,
    },
    priceRange: {
      "0-25": false,
      "25-50": false,
      "50-75": false,
    },
  });

  const categoryOptions = useMemo(
    () => [
      "Chaat Time",
      "Tea & Snacks",
      "Milkshakes",
      "Puffs",
      "Cakes (1 KG)",
      "Pastries",
      "Special Cakes",
      "Jar Cakes",
      "Brownies",
      "Tres Leches",
      "Custom Cakes",
      "Wedding Cakes",
    ],
    []
  );

  const priceRangeOptions = useMemo(() => ["0-25", "25-50", "50-75"], []);

  const applyFilters = useCallback(
    (next) => {
      setFilters(next);
      onFilterChange?.(next);
    },
    [onFilterChange]
  );

  const handleFilterChange = (type, value) => {
    applyFilters({
      ...filters,
      [type]: value,
    });
  };

  const handleReset = () => {
    const reset = {
      categories: Object.fromEntries(categoryOptions.map((c) => [c, false])),
      priceRange: Object.fromEntries(priceRangeOptions.map((p) => [p, false])),
    };
    applyFilters(reset);
  };

  const toggle = () => setIsOpen((v) => !v);

  /* ---------- Presentational helpers ---------- */
  const CheckboxItem = ({ label, checked, onChange, showBorder = false }) => (
    <div
      className={`flex justify-between items-center p-3 cursor-pointer hover:bg-gradient-to-r hover:from-orange-50 hover:to-red-50 rounded-lg transition-all duration-300 ${
        showBorder ? "border-b border-orange-200" : ""
      } group`}
    >
      <label
        className="text-sm text-cyan-900 cursor-pointer flex-1 group-hover:text-red-600 transition-colors duration-300"
        style={{ fontFamily: "'Foolish Talk', cursive" }}
        onClick={() => onChange(!checked)}
      >
        {label}
      </label>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
          aria-label={`Filter by ${label}`}
        />
        <motion.div
          className={`w-5 h-5 rounded-lg cursor-pointer transition-all duration-300 border-2 ${
            checked
              ? "bg-gradient-to-r from-red-500 to-orange-500 border-red-500 shadow-lg"
              : "bg-white border-orange-300 hover:border-red-400"
          }`}
          onClick={() => onChange(!checked)}
          role="checkbox"
          aria-checked={checked}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onChange(!checked);
            }
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        />
      </div>
    </div>
  );

  const FilterSection = ({ title, children, icon, delay = 0 }) => (
    <motion.section
      className="flex overflow-hidden flex-col bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl w-full border-2 border-orange-200 relative"
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, delay }}
      whileHover={{ y: -3, boxShadow: "0 20px 40px rgba(0,0,0,0.08)" }}
    >
      <motion.header className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-orange-100 to-red-100 relative z-10">
        <div className="flex items-center gap-3">
          <motion.span
            className="text-2xl"
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            {icon}
          </motion.span>
          <h2
            className="text-lg font-bold text-red-500"
            style={{ fontFamily: "'Sansita Swashed', cursive" }}
          >
            {title}
          </h2>
        </div>
        <motion.div
          className="text-yellow-400"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          ✨
        </motion.div>
      </motion.header>

      <div className="flex flex-col pb-3 mx-auto my-0 w-[255px] max-xl:w-full relative z-10">
        {children}
      </div>
    </motion.section>
  );

  const ChevronIcon = ({ open }) => (
    <motion.svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      animate={{ rotate: open ? 180 : 0 }}
      transition={{ duration: 0.25 }}
      className="text-cyan-900"
    >
      <path
        d="M6 9L12 15L18 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  );

  return (
    <aside className="flex flex-col gap-7 w-[285px] xl:w-[285px] max-xl:w-full">
      {/* Header (toggle visible only on mobile/tablet) */}
      <motion.header
        className="flex justify-between items-center w-full bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-orange-200"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-4">
          <motion.span
            className="text-3xl"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            🔍
          </motion.span>
          <h1
            className="text-2xl font-bold text-cyan-900"
            style={{ fontFamily: "'Sansita Swashed', cursive" }}
          >
            Filter
          </h1>

        </div>

        {/* Mobile toggle */}
        {!isDesktop && (
          <motion.button
            onClick={toggle}
            className="flex items-center justify-center p-2 text-cyan-900 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-xl bg-orange-50 hover:bg-red-50 transition-all duration-200"
            aria-label={isOpen ? "Close filters" : "Open filters"}
            aria-expanded={isOpen}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronIcon open={isOpen} />
          </motion.button>
        )}

        {/* Reset: always shown on desktop; on mobile shown when panel is open */}
        {(isDesktop || isOpen) && (
          <motion.button
            className="ml-auto px-4 py-2 text-base font-bold text-red-500 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-orange-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-xl transition-all duration-200 border-2 border-red-200 hover:border-red-500"
            onClick={handleReset}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            style={{ fontFamily: "'Foolish Talk', cursive" }}
          >
            Reset ✨
          </motion.button>
        )}
      </motion.header>

      {/* Content: expanded on desktop, collapsible on mobile */}
      {isDesktop ? (
        // Desktop: always visible
        <div className="flex flex-col gap-7">
          <FilterSection title="Categories" icon="🎂" delay={0.1}>
            {categoryOptions.map((option, i) => (
              <CheckboxItem
                key={option}
                label={option}
                checked={filters.categories[option]}
                onChange={(checked) =>
                  handleFilterChange("categories", {
                    ...filters.categories,
                    [option]: checked,
                  })
                }
                showBorder={i < categoryOptions.length - 1}
              />
            ))}
          </FilterSection>

          <FilterSection title="Price Range" icon="💰" delay={0.15}>
            {priceRangeOptions.map((option, i) => (
              <CheckboxItem
                key={option}
                label={`$${option.replace("-", " - $")}`}
                checked={filters.priceRange[option]}
                onChange={(checked) =>
                  handleFilterChange("priceRange", {
                    ...filters.priceRange,
                    [option]: checked,
                  })
                }
                showBorder={i < priceRangeOptions.length - 1}
              />
            ))}
          </FilterSection>
        </div>
      ) : (
        // Mobile/Tablet: animated collapse
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="filters-panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden flex flex-col gap-7"
            >
              <FilterSection title="Categories" icon="🎂" delay={0.05}>
                {categoryOptions.map((option, i) => (
                  <CheckboxItem
                    key={option}
                    label={option}
                    checked={filters.categories[option]}
                    onChange={(checked) =>
                      handleFilterChange("categories", {
                        ...filters.categories,
                        [option]: checked,
                      })
                    }
                    showBorder={i < categoryOptions.length - 1}
                  />
                ))}
              </FilterSection>

              <FilterSection title="Price Range" icon="💰" delay={0.08}>
                {priceRangeOptions.map((option, i) => (
                  <CheckboxItem
                    key={option}
                    label={`$${option.replace("-", " - $")}`}
                    checked={filters.priceRange[option]}
                    onChange={(checked) =>
                      handleFilterChange("priceRange", {
                        ...filters.priceRange,
                        [option]: checked,
                      })
                    }
                    showBorder={i < priceRangeOptions.length - 1}
                  />
                ))}
              </FilterSection>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </aside>
  );
};

export default FilterSidebar;
